// ===========================================
// RecentContratsTable — Tableau des derniers contrats créés
// Recherche + date relative + actions inline
// ===========================================
import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Table, Tag, Empty, Button, Input, Tooltip } from 'antd';
import {
    FileProtectOutlined,
    EyeOutlined,
    ArrowRightOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { formatMontant, formatDate } from '../../../common/utils/format';
import { CONTRAT_STATUTS } from '../../../common/constants/statuts';

const getClientLabel = (client) => {
    if (!client) return '—';
    if (client.raisonSociale) return client.raisonSociale;
    return `${client.prenom || ''} ${client.nom || ''}`.trim() || client.code || '—';
};

const StatusTag = ({ statut }) => {
    const cfg = CONTRAT_STATUTS[statut] || { color: 'default', label: statut };
    return <Tag color={cfg.color}>{cfg.label}</Tag>;
};

// ─── Formate une date en relatif ("il y a 1h", "il y a 2 jours") ──────────
const formatRelative = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 0) return formatDate(date);

    const diffMin   = Math.floor(diffMs / 60000);
    const diffHr    = Math.floor(diffMs / 3600000);
    const diffDay   = Math.floor(diffMs / 86400000);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear  = Math.floor(diffDay / 365);

    if (diffMin < 1)    return "À l'instant";
    if (diffMin < 60)   return `Il y a ${diffMin} min`;
    if (diffHr  < 24)   return `Il y a ${diffHr} h`;
    if (diffDay < 30)   return diffDay === 1 ? 'Hier' : `Il y a ${diffDay} jours`;
    if (diffMonth < 12) return `Il y a ${diffMonth} mois`;
    return `Il y a ${diffYear} an${diffYear > 1 ? 's' : ''}`;
};

const RecentContratsTable = ({ data, loading }) => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');

    // ─── Filtrage côté client (n° police, client, type, compagnie) ────
    const filteredData = useMemo(() => {
        if (!data) return [];
        const term = search.trim().toLowerCase();
        if (!term) return data;

        return data.filter((c) => {
            const fields = [
                c.numeroPolice,
                getClientLabel(c.client),
                c.typeAssurance?.nom,
                c.compagnie?.nom,
            ];
            return fields.some((f) => (f || '').toString().toLowerCase().includes(term));
        });
    }, [data, search]);

    const columns = [
        {
            title: 'N° Police',
            dataIndex: 'numeroPolice',
            key: 'numeroPolice',
            render: (val, row) => (
                <Link
                    to={`/dashboard/contrat/${row._id}`}
                    className="recent-contrats-numero"
                >
                    {val || '—'}
                </Link>
            ),
            width: 130,
        },
        {
            title: 'Client',
            key: 'client',
            render: (_, row) => (
                <span className="recent-contrats-client">{getClientLabel(row.client)}</span>
            ),
            ellipsis: true,
        },
        {
            title: 'Type',
            key: 'type',
            render: (_, row) => (
                <Tag color="blue">{row.typeAssurance?.nom || '—'}</Tag>
            ),
            width: 140,
            responsive: ['md'],
        },
        {
            title: 'Compagnie',
            key: 'compagnie',
            render: (_, row) => row.compagnie?.nom || '—',
            width: 160,
            responsive: ['lg'],
            ellipsis: true,
        },
        {
            title: 'Prime TTC',
            dataIndex: 'primeTTC',
            key: 'primeTTC',
            render: (val) => (
                <span className="recent-contrats-amount">{formatMontant(val || 0)} F</span>
            ),
            align: 'right',
            width: 130,
        },
        {
            title: 'Créé',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (val) => (
                <Tooltip title={val ? formatDate(val) : '—'}>
                    <span className="recent-contrats-relative">{formatRelative(val)}</span>
                </Tooltip>
            ),
            width: 140,
            responsive: ['md'],
        },
        {
            title: 'Statut',
            dataIndex: 'statut',
            key: 'statut',
            render: (val) => <StatusTag statut={val} />,
            width: 110,
        },
        {
            title: 'Action',
            key: 'actions',
            render: (_, row) => (
                <Button
                    size="small"
                    type="primary"
                    icon={<EyeOutlined />}
                    className="btn-detail recent-contrats-btn-detail"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dashboard/contrat/${row._id}`);
                    }}
                >
                    Détail
                </Button>
            ),
            width: 110,
            align: 'center',
        },
    ];

    return (
        <Card
            className="dashboard-op-card recent-contrats-card"
            title={
                <div className="chart-card-title">
                    <FileProtectOutlined />
                    <span>Derniers contrats créés</span>
                    {data?.length > 0 && (
                        <span className="op-card-count">{data.length}</span>
                    )}
                </div>
            }
            extra={
                <Link to="/dashboard/contrats" className="recent-contrats-link">
                    Voir tous <ArrowRightOutlined />
                </Link>
            }
        >
            {/* Toolbar : barre de recherche */}
            <div className="recent-contrats-toolbar">
                <Input
                    allowClear
                    prefix={<SearchOutlined style={{ color: 'var(--cap-text-muted)' }} />}
                    placeholder="Rechercher par N° police, client, type ou compagnie..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="recent-contrats-search"
                    aria-label="Rechercher dans les derniers contrats"
                />
                {search && (
                    <span className="recent-contrats-search-count">
                        {filteredData.length} résultat{filteredData.length > 1 ? 's' : ''}
                    </span>
                )}
            </div>

            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="_id"
                loading={loading}
                pagination={false}
                size="middle"
                locale={{
                    emptyText: (
                        <Empty
                            description={
                                search
                                    ? `Aucun contrat ne correspond à "${search}"`
                                    : 'Aucun contrat récent'
                            }
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    ),
                }}
                scroll={{ x: 'max-content' }}
                onRow={(record) => ({
                    onClick: () => navigate(`/dashboard/contrat/${record._id}`),
                    style: { cursor: 'pointer' },
                })}
            />
        </Card>
    );
};

export default RecentContratsTable;
